import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Link2, Plus, RefreshCw } from 'lucide-react';
import {
  useCreatePublisher,
  useMapPublisherVideo,
  usePublisherVideos,
  usePublishers,
  useRotatePublisherKey,
  useUnmapPublisherVideo,
  useUpdatePublisher,
  useVideos,
} from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { PublisherOut, PublisherStatus, PublisherVideoOut } from '@/api/types';
import { PUBLISHER_STATUSES } from '@/api/types';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { CodeBlock } from '@/components/arc/code-block/code-block';
import { ConfirmMorph } from '@/components/arc/confirm-morph/confirm-morph';
import { CopyButton } from '@/components/arc/copy-button/copy-button';
import { Dialog, DialogContent } from '@/components/arc/dialog/dialog';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { Input } from '@/components/arc/input/input';
import { NumberField } from '@/components/arc/number-field/number-field';
import { SearchField } from '@/components/arc/search-field/search-field';
import SegmentedControl from '@/components/arc/segmented-control/segmented-control';
import { Select } from '@/components/arc/select/select';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { TagInput } from '@/components/arc/tag-input/tag-input';
import { PublisherStatusBadge } from '@/components/status/StatusBadge';
import { fmtDate, labelize, truncate } from '@/lib/format';
import { useDebounce } from '@/lib/hooks';
import { useHref } from '@/module/basePath';
import { useTimezone } from '@/module/MomentProvider';
import { LookupCard, QualityCell } from './PublisherQuality';
import styles from './overlay.module.css';

type PublisherRow = { [K in keyof PublisherOut]: PublisherOut[K] };
type MappingRow = { [K in keyof PublisherVideoOut]: PublisherVideoOut[K] };
/** Router state sent by the discovery page's "Add as publisher" action. */
type PrefillState = { addPublisherDomain?: string; addPublisherName?: string } | null;
type Prefill = { name: string; domain: string };

export function PublishersPage() {
  const tz = useTimezone();
  const pubs = usePublishers();
  const location = useLocation();
  const navigate = useNavigate();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PublisherOut | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [editorRun, setEditorRun] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const items = pubs.data?.items ?? [];
  const selected = items.find((p) => p.id === selectedId) ?? null;
  const isEmpty = !pubs.isLoading && !pubs.isError && items.length === 0;

  const openEditor = (publisher: PublisherOut | null, initial: Prefill | null = null) => {
    setEditing(publisher);
    setPrefill(initial);
    setEditorRun((n) => n + 1);
    setEditorOpen(true);
  };

  // Arriving from discovery: open the add dialog with the domain filled in, then clear the router state.
  const state = location.state as PrefillState;
  const prefillDomain = state?.addPublisherDomain;
  const prefillName = state?.addPublisherName;
  useEffect(() => {
    if (!prefillDomain) return;
    openEditor(null, { name: prefillName ?? prefillDomain, domain: prefillDomain });
    void navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillDomain, prefillName]);

  const columns: DataColumn<PublisherRow>[] = [
    {
      key: 'name',
      label: 'Publisher',
      render: (_, p) => (
        <div className={styles.cell}>
          <span>{p.name}</span>
          <span className={styles.cellSub}>
            {p.domain}
            {p.extra_domains.length ? ` and ${p.extra_domains.length} more` : ''}
          </span>
        </div>
      ),
    },
    { key: 'status', label: 'Status', render: (_, p) => <PublisherStatusBadge status={p.status} /> },
    { key: 'quality', label: 'Quality', sortable: false, render: (_, p) => <QualityCell domain={p.domain} /> },
    {
      key: 'sdk_key',
      label: 'SDK key',
      sortable: false,
      render: (_, p) => (
        <span className={styles.keyRow}>
          <code className={styles.keyCode}>{truncate(p.sdk_key, 18)}</code>
          <CopyButton value={p.sdk_key} label="Copy SDK key" iconOnly variant="plain" />
        </span>
      ),
    },
    { key: 'revenue_share_pct', label: 'Revenue share', numeric: true, render: (v) => `${String(v)}%` },
    { key: 'video_count', label: 'Videos', numeric: true },
    { key: 'created_at', label: 'Created', render: (v) => fmtDate(String(v), tz) },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, p) => (
        <span className={styles.rowActions}>
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={selectedId === p.id}
            onClick={() => setSelectedId(selectedId === p.id ? null : p.id)}
          >
            {selectedId === p.id ? 'Hide details' : 'Show details'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openEditor(p)}>
            Edit
          </Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <section className="stack-4" aria-labelledby="publishers-heading">
        <div className={styles.sectionHead}>
          <div className={styles.cell}>
            <h2 id="publishers-heading" className={styles.sectionTitle}>
              Publishers
            </h2>
            <span className={styles.sectionMeta}>
              Sites that embed the Scenewise SDK and map their player video ids to analyzed videos
            </span>
          </div>
          {!isEmpty && (
            <Button onClick={() => openEditor(null)}>
              <Plus size={16} strokeWidth={1.75} aria-hidden="true" /> Add publisher
            </Button>
          )}
        </div>

        {pubs.isLoading ? (
          <Skeleton lines={5} label="Loading publishers" />
        ) : pubs.isError ? (
          <Alert tone="danger" title="Publishers did not load">
            {errorMessage(pubs.error)}{' '}
            <Button variant="secondary" size="sm" onClick={() => void pubs.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : isEmpty ? (
          <EmptyState
            title="No publishers yet"
            description="Add a publisher to get an SDK key and the install snippet for its pages."
            action={
              <Button onClick={() => openEditor(null)}>
                <Plus size={16} strokeWidth={1.75} aria-hidden="true" /> Add publisher
              </Button>
            }
          />
        ) : (
          <div className="table-scroll">
            <SortableDataTable<PublisherRow>
              rows={items}
              columns={columns}
              rowKey="id"
              caption="Publishers"
              defaultSort={{ key: 'name', direction: 'asc' }}
              itemName={{ one: 'publisher', other: 'publishers' }}
            />
          </div>
        )}
      </section>

      {selected && (
        <PublisherDetail
          key={selected.id}
          publisher={selected}
          onEdit={() => openEditor(selected)}
          onClose={() => setSelectedId(null)}
        />
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent
          title={editing ? 'Edit publisher' : 'Add publisher'}
          description={
            editing
              ? 'Changes apply to the SDK on the next page load.'
              : 'The domain is used for the CORS and Origin check. Subdomains are allowed.'
          }
        >
          <PublisherForm
            key={`${editing?.id ?? 'new'}:${editorRun}`}
            publisher={editing}
            prefill={prefill}
            onDone={() => setEditorOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function PublisherForm({
  publisher,
  prefill,
  onDone,
}: {
  publisher: PublisherOut | null;
  prefill?: Prefill | null;
  onDone: () => void;
}) {
  const create = useCreatePublisher();
  const update = useUpdatePublisher();
  const [name, setName] = useState(publisher?.name ?? prefill?.name ?? '');
  const [domain, setDomain] = useState(publisher?.domain ?? prefill?.domain ?? '');
  const lookupDomain = useDebounce(domain, 600);
  const [extra, setExtra] = useState<string[]>(publisher?.extra_domains ?? []);
  const [share, setShare] = useState(publisher?.revenue_share_pct ?? 50);
  const [status, setStatus] = useState<PublisherStatus>(publisher?.status ?? 'active');
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const valid = name.trim() !== '' && domain.trim() !== '';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || pending) return;
    try {
      if (publisher) {
        await update.mutateAsync({
          id: publisher.id,
          body: {
            name: name.trim(),
            domain: domain.trim(),
            extra_domains: extra,
            revenue_share_pct: share,
            status,
          },
        });
      } else {
        await create.mutateAsync({
          name: name.trim(),
          domain: domain.trim(),
          extra_domains: extra,
          revenue_share_pct: share,
        });
      }
      onDone();
    } catch {
      /* shown in the alert below */
    }
  };

  return (
    <form className={styles.form} onSubmit={(e) => void submit(e)}>
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
      <Input
        label="Domain"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        placeholder="publisher.com"
        required
        autoCapitalize="none"
        spellCheck={false}
      />
      <LookupCard domain={lookupDomain} />
      <TagInput
        label="Extra domains"
        value={extra}
        onValueChange={setExtra}
        placeholder="cdn.publisher.com"
        description="Press Enter after each domain"
      />
      <NumberField
        label="Revenue share"
        value={share}
        onValueChange={setShare}
        min={0}
        max={100}
        suffix="%"
      />
      {publisher && (
        <Select
          label="Status"
          value={status}
          onValueChange={(v) => setStatus(v as PublisherStatus)}
          options={PUBLISHER_STATUSES.map((s) => ({ value: s, label: labelize(s) }))}
        />
      )}
      {error && (
        <Alert tone="danger" title="Could not save the publisher">
          {errorMessage(error)}
        </Alert>
      )}
      <div className={styles.footer}>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" loading={pending} disabled={!valid}>
          {publisher ? 'Save publisher' : 'Create publisher'}
        </Button>
      </div>
    </form>
  );
}

function PublisherDetail({
  publisher,
  onEdit,
  onClose,
}: {
  publisher: PublisherOut;
  onEdit: () => void;
  onClose: () => void;
}) {
  const rotate = useRotatePublisherKey();
  const headingId = `publisher-${publisher.id}`;
  const domains = [publisher.domain, ...publisher.extra_domains].join(', ');

  return (
    <section className={styles.detail} aria-labelledby={headingId}>
      <div className={styles.sectionHead}>
        <div className={styles.cell}>
          <h2 id={headingId} className={styles.sectionTitle}>
            {publisher.name}
          </h2>
          <span className={styles.sectionMeta}>
            {domains}. {publisher.revenue_share_pct}% revenue share.
          </span>
        </div>
        <div className={styles.headActions}>
          <PublisherStatusBadge status={publisher.status} />
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Edit publisher
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Hide details
          </Button>
        </div>
      </div>

      <div className={styles.detailGrid}>
        <div className="stack-4">
          <h3 className={styles.subTitle}>SDK key</h3>
          <div className={styles.keyRow}>
            <code className={styles.keyCode}>{publisher.sdk_key}</code>
            <CopyButton value={publisher.sdk_key} label="Copy key" />
          </div>
          <div className={styles.keyRow}>
            <ConfirmMorph
              label="Rotate key"
              icon={<RefreshCw size={16} strokeWidth={1.75} />}
              tone="neutral"
              prompt="Rotate the SDK key?"
              confirmLabel="Rotate"
              pendingLabel="Rotating"
              doneLabel="Key rotated"
              errorLabel="Rotation failed"
              onConfirm={() => rotate.mutateAsync(publisher.id)}
            />
          </div>
          <p className="text-muted text-xs">
            Pages still using the old key stop loading overlays once it is rotated.
          </p>
        </div>
        <div className="stack-4">
          <h3 className={styles.subTitle}>Install snippet</h3>
          <CodeBlock code={publisher.embed_snippet} language="html" filename="index.html" maxLines={8} />
        </div>
      </div>

      <PublisherVideos publisher={publisher} />
    </section>
  );
}

function PublisherVideos({ publisher }: { publisher: PublisherOut }) {
  const href = useHref();
  const tz = useTimezone();
  const list = usePublisherVideos(publisher.id);
  const unmap = useUnmapPublisherVideo();
  const [open, setOpen] = useState(false);
  const [run, setRun] = useState(0);
  const rows = list.data?.items ?? [];

  const columns: DataColumn<MappingRow>[] = [
    {
      key: 'player_video_id',
      label: 'Player video id',
      render: (v) => <code className={styles.keyCode}>{String(v)}</code>,
    },
    {
      key: 'video_id',
      label: 'Scenewise video',
      render: (v) => (
        <Link to={href(`/videos/${String(v)}`)} className={styles.link} title={String(v)}>
          <code className={styles.keyCode}>{truncate(String(v), 14)}</code>
        </Link>
      ),
    },
    {
      key: 'page_url',
      label: 'Page',
      render: (v) =>
        v ? (
          <a href={String(v)} target="_blank" rel="noreferrer" title={String(v)} className={styles.link}>
            {truncate(String(v), 40)}
          </a>
        ) : (
          <span className="text-muted">No page URL</span>
        ),
    },
    { key: 'created_at', label: 'Mapped', render: (v) => fmtDate(String(v), tz) },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, r) => (
        <span className={styles.rowActions}>
          <ConfirmMorph
            label="Unmap"
            prompt="Unmap this video?"
            confirmLabel="Unmap"
            pendingLabel="Unmapping"
            doneLabel="Unmapped"
            errorLabel="Could not unmap"
            onConfirm={() => unmap.mutateAsync({ id: publisher.id, mappingId: r.id })}
          />
        </span>
      ),
    },
  ];

  const openDialog = () => {
    setRun((n) => n + 1);
    setOpen(true);
  };

  return (
    <div className="stack-4">
      <div className={styles.sectionHead}>
        <div className={styles.cell}>
          <h3 className={styles.subTitle}>Mapped videos</h3>
          <span className={styles.sectionMeta}>The SDK looks up overlay manifests by player video id.</span>
        </div>
        <Button variant="secondary" size="sm" onClick={openDialog}>
          <Link2 size={16} strokeWidth={1.75} aria-hidden="true" /> Map video
        </Button>
      </div>

      {list.isLoading ? (
        <Skeleton lines={3} label="Loading mapped videos" />
      ) : list.isError ? (
        <Alert tone="danger" title="Mapped videos did not load">
          {errorMessage(list.error)}{' '}
          <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No videos mapped"
          description="Map a player video id to an analyzed video so the SDK can find its moments."
          action={
            <Button variant="secondary" onClick={openDialog}>
              Map video
            </Button>
          }
        />
      ) : (
        <div className="table-scroll">
          <SortableDataTable<MappingRow>
            rows={rows}
            columns={columns}
            rowKey="id"
            caption={`Videos mapped for ${publisher.name}`}
            itemName={{ one: 'mapping', other: 'mappings' }}
          />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title="Map player video"
          description="Connect a video on the publisher page to a Scenewise video."
        >
          <MapVideoForm key={run} publisher={publisher} onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MapVideoForm({ publisher, onDone }: { publisher: PublisherOut; onDone: () => void }) {
  const map = useMapPublisherVideo();
  const [playerId, setPlayerId] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [mode, setMode] = useState<'pick' | 'url'>('pick');
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 300);
  const [videoId, setVideoId] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const videos = useVideos({ q: dq || undefined, limit: 50 });
  const options = (videos.data?.pages.flatMap((p) => p.items) ?? []).map((v) => ({
    value: v.id,
    label: `${v.title ?? v.external_id} (${labelize(v.analysis_status)})`,
  }));
  const valid = playerId.trim() !== '' && (mode === 'pick' ? videoId !== '' : videoUrl.trim() !== '');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || map.isPending) return;
    try {
      await map.mutateAsync({
        id: publisher.id,
        body: {
          player_video_id: playerId.trim(),
          page_url: pageUrl.trim() || null,
          ...(mode === 'pick' ? { video_id: videoId } : { video_url: videoUrl.trim() }),
        },
      });
      onDone();
    } catch {
      /* shown in the alert below */
    }
  };

  return (
    <form className={styles.form} onSubmit={(e) => void submit(e)}>
      <Input
        label="Player video id"
        value={playerId}
        onChange={(e) => setPlayerId(e.target.value)}
        description="The data-moment-video-id on the publisher page, or the page URL"
        required
        autoFocus
      />
      <Input
        label="Page URL"
        value={pageUrl}
        onChange={(e) => setPageUrl(e.target.value)}
        placeholder="https://publisher.com/article"
      />
      <SegmentedControl
        label="Video source"
        value={mode}
        onValueChange={(v) => setMode(v as 'pick' | 'url')}
        options={[
          { value: 'pick', label: 'Analyzed video' },
          { value: 'url', label: 'Video URL' },
        ]}
      />
      {mode === 'pick' ? (
        <>
          <SearchField label="Search videos" value={q} onValueChange={setQ} placeholder="Title or channel" />
          <Select
            label="Video"
            value={videoId}
            onValueChange={setVideoId}
            placeholder={
              videos.isLoading ? 'Loading videos' : options.length ? 'Select a video' : 'No videos match'
            }
            options={options}
            disabled={options.length === 0}
          />
        </>
      ) : (
        <Input
          label="Video URL"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=... or https://cdn.example.com/video.mp4"
          description="Creates the video in Scenewise and starts its analysis"
          required
        />
      )}
      {map.error && (
        <Alert tone="danger" title="Could not map the video">
          {errorMessage(map.error)}
        </Alert>
      )}
      <div className={styles.footer}>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" loading={map.isPending} disabled={!valid}>
          Map video
        </Button>
      </div>
    </form>
  );
}
